import {
  activeCodAgeConditions,
  ALL_COLUMNS,
  emptyCodAgeFilter,
  isCodAgeFilterValue,
  matchesCodAge,
  type CodAgeFilterValue,
  type ProgrammaticFilterColumn,
} from "@/dashboard/inventory_management/common/portfolioTableColumns";
import { FilterMatchMode, FilterService } from "@primevue/core/api";

// The PortfolioItemsTable filter model: which columns filter, how each one's value is shaped, and
// the value algebra the table, its chips and its URL encoding share.

/**
 * How a column filters: `list` is an include-list (`[]` = no filter), `text` a substring and
 * `codAge` one or two COD-age conditions.
 */
type FilterKind = "list" | "text" | "codAge";

/** A list filter's element: the filter options' values, e.g. `null` for production support "Unknown". */
export type FilterScalar = string | boolean | null;

interface FilterValueByKind {
  list: FilterScalar[];
  text: string | null;
  codAge: CodAgeFilterValue;
}

/** The "no value" option of the price ("-") and ecolabel ("None") filters. */
export const NONE_FILTER_VALUE = "__none__";

export interface FilterOption {
  label: string;
  value: FilterScalar;
}

interface FilterColumnSpec {
  kind: FilterKind;
  /** A PrimeVue match mode, or one of the custom matchers registered at the bottom of this file. */
  matchMode: string;
  /** i18n key of the column header, which also names the column in its chip. */
  header: string;
}

const FILTER_COLUMN_SPECS = {
  company_name: {
    kind: "text",
    matchMode: FilterMatchMode.CONTAINS,
    header: "charts.fields.company_name",
  },
  account_emails: {
    kind: "list",
    matchMode: "accountEmails",
    header: ALL_COLUMNS.account_emails.header,
  },
  technology_type: {
    kind: "list",
    matchMode: FilterMatchMode.IN,
    header: ALL_COLUMNS.technology_type.header,
  },
  production_month: {
    kind: "list",
    matchMode: FilterMatchMode.IN,
    header: ALL_COLUMNS.production_month.header,
  },
  status: {
    kind: "list",
    matchMode: FilterMatchMode.IN,
    header: ALL_COLUMNS.status.header,
  },
  portfolio_name: {
    kind: "list",
    matchMode: FilterMatchMode.IN,
    header: ALL_COLUMNS.portfolio_name.header,
  },
  portfolio_type: {
    kind: "list",
    matchMode: FilterMatchMode.IN,
    header: ALL_COLUMNS.portfolio_type.header,
  },
  registration_authority: {
    kind: "list",
    matchMode: FilterMatchMode.IN,
    header: ALL_COLUMNS.registration_authority.header,
  },
  registry_region: {
    kind: "list",
    matchMode: FilterMatchMode.IN,
    header: ALL_COLUMNS.registry_region.header,
  },
  issuing_region: {
    kind: "list",
    matchMode: FilterMatchMode.IN,
    header: ALL_COLUMNS.issuing_region.header,
  },
  production_device_name: {
    kind: "list",
    matchMode: FilterMatchMode.IN,
    header: ALL_COLUMNS.production_device_name.header,
  },
  production_support: {
    kind: "list",
    matchMode: FilterMatchMode.IN,
    header: ALL_COLUMNS.production_support.header,
  },
  ecolabel_tags: {
    kind: "list",
    matchMode: "ecolabel",
    header: ALL_COLUMNS.ecolabel_tags.header,
  },
  cod_age: {
    kind: "codAge",
    matchMode: "codAge",
    header: ALL_COLUMNS.cod_age.header,
  },
  limit_price: {
    kind: "list",
    matchMode: "limitPrice",
    header: ALL_COLUMNS.limit_price.header,
  },
  sale_price: {
    kind: "list",
    matchMode: "salePrice",
    header: ALL_COLUMNS.sale_price.header,
  },
  registry_id: {
    kind: "text",
    matchMode: FilterMatchMode.CONTAINS,
    header: ALL_COLUMNS.registry_id.header,
  },
  bundle_start: {
    kind: "text",
    matchMode: FilterMatchMode.CONTAINS,
    header: ALL_COLUMNS.bundle_start.header,
  },
  bundle_end: {
    kind: "text",
    matchMode: FilterMatchMode.CONTAINS,
    header: ALL_COLUMNS.bundle_end.header,
  },
  transfer_deadline: {
    kind: "list",
    matchMode: FilterMatchMode.IN,
    header: ALL_COLUMNS.transfer_deadline.header,
  },
} as const satisfies Record<string, FilterColumnSpec>;

export type FilterColumn = keyof typeof FILTER_COLUMN_SPECS;

type FilterKindOf<C extends FilterColumn> =
  (typeof FILTER_COLUMN_SPECS)[C]["kind"];

/**
 * Every filterable column, in the table's column order. `count` is not one: its filter cell shows
 * the filtered total. Typed column by column so generic code can follow a column to its kind.
 */
export const FILTER_COLUMNS: {
  readonly [C in FilterColumn]: FilterColumnSpec & { kind: FilterKindOf<C> };
} = FILTER_COLUMN_SPECS;

/** The value a column's filter holds; without a column, any column's. */
export type FilterValue<C extends FilterColumn = FilterColumn> =
  FilterValueByKind[FilterKindOf<C>];

/** Every column's filter value. */
export type FilterState = { [C in FilterColumn]: FilterValue<C> };

/** The filters a page sets programmatically, e.g. the criteria a cancellation document asks for. */
export type PortfolioColumnFilters = Partial<
  Pick<FilterState, ProgrammaticFilterColumn>
>;

/** PrimeVue DataTable's `filters` model. The filter editors edit each entry's `value` in place. */
export type DataTableFilters = {
  [C in FilterColumn]: { value: FilterValue<C>; matchMode: string };
};

/** One handler per filter kind, each given the value narrowed to its kind. */
type FilterKindHandlers<R> = {
  [K in FilterKind]: (value: FilterValueByKind[K]) => R;
};

export const isFilterColumn = (key: string): key is FilterColumn =>
  Object.hasOwn(FILTER_COLUMN_SPECS, key);

export const FILTER_COLUMN_KEYS: readonly FilterColumn[] =
  Object.keys(FILTER_COLUMN_SPECS).filter(isFilterColumn);

/** Calls the handler for the column's filter kind with its value. */
export const matchFilterKind = <C extends FilterColumn, R>(
  column: C,
  value: FilterValue<C>,
  handlers: FilterKindHandlers<R>
): R => handlers[FILTER_COLUMNS[column].kind](value);

/** The value of a column with no filter: `[]`, `null`, or a COD filter with no year picked. */
export const emptyFilterValue = <C extends FilterColumn>(
  column: C
): FilterValue<C> => kindModel(column).empty();

/** A copy safe to hand to a filter editor, which mutates the value in place. */
export const cloneFilterValue = <C extends FilterColumn>(
  column: C,
  value: FilterValue<C>
): FilterValue<C> => kindModel(column).clone(value);

/** True when a filter value narrows the table; `undefined` (no value at all) never does. */
export const isFilterValueActive = <C extends FilterColumn>(
  column: C,
  value: FilterValue<C> | undefined
): boolean => value !== undefined && kindModel(column).isActive(value);

/**
 * Equality of two filter values of a column: lists compare as sets, by `String()` so a value that
 * went through JSON still equals its option, and COD filters by their active conditions. Two
 * inactive values are equal whatever their shape, so a cleared list (`[]`) matches a column that
 * has no default at all (`undefined`).
 */
export const filterValuesEqual = <C extends FilterColumn>(
  column: C,
  a: FilterValue<C> | undefined,
  b: FilterValue<C> | undefined
): boolean => {
  const left = a ?? emptyFilterValue(column);
  const right = b ?? emptyFilterValue(column);
  const leftActive = isFilterValueActive(column, left);
  const rightActive = isFilterValueActive(column, right);
  if (!leftActive || !rightActive) return leftActive === rightActive;
  return kindModel(column).equal(left, right);
};

/**
 * Reads an untrusted stored value (e.g. from the URL) as the column's filter value.
 *
 * @returns The value, or `undefined` when its shape doesn't fit the column's kind. A list keeps
 *   only its scalar elements.
 */
export const parseStoredFilterValue = <C extends FilterColumn>(
  column: C,
  raw: unknown
): FilterValue<C> | undefined => kindModel(column).parse(raw);

/**
 * The DataTable's `filters` model for these values. Each value is a fresh copy, because the filter
 * editors edit the DataTable's (shallow) copy of the model in place.
 */
export const toDataTableFilters = (values: FilterState): DataTableFilters =>
  // Object.fromEntries can't carry each column's own value type.
  Object.fromEntries(
    FILTER_COLUMN_KEYS.map((column) => [
      column,
      {
        value: cloneFilterValue(column, values[column]),
        matchMode: FILTER_COLUMNS[column].matchMode,
      },
    ])
  ) as DataTableFilters;

interface FilterKindModel<V> {
  empty: () => V;
  clone: (value: V) => V;
  isActive: (value: V) => boolean;
  /** Equality of two active values. */
  equal: (a: V, b: V) => boolean;
  parse: (raw: unknown) => V | undefined;
}

const isFilterScalar = (value: unknown): value is FilterScalar =>
  value === null || typeof value === "string" || typeof value === "boolean";

const codAgeKey = (filter: CodAgeFilterValue): string =>
  activeCodAgeConditions(filter)
    .map((condition) => `${condition.op}${condition.value}`)
    .sort()
    .join("&");

const FILTER_KINDS: {
  [K in FilterKind]: FilterKindModel<FilterValueByKind[K]>;
} = {
  list: {
    empty: () => [],
    clone: (values) => [...values],
    isActive: (values) => values.length > 0,
    equal: (a, b) => {
      const keys = new Set(a.map(String));
      return (
        b.length === a.length && b.every((value) => keys.has(String(value)))
      );
    },
    parse: (raw) =>
      Array.isArray(raw) ? raw.filter(isFilterScalar) : undefined,
  },
  text: {
    empty: () => null,
    clone: (text) => text,
    isActive: (text) => text !== null && text.trim() !== "",
    equal: (a, b) => a === b,
    parse: (raw) => (typeof raw === "string" ? raw : undefined),
  },
  codAge: {
    empty: emptyCodAgeFilter,
    clone: ({ a, b }) => ({ a: { ...a }, b: b ? { ...b } : null }),
    isActive: (filter) => activeCodAgeConditions(filter).length > 0,
    equal: (a, b) => codAgeKey(a) === codAgeKey(b),
    parse: (raw) => (isCodAgeFilterValue(raw) ? raw : undefined),
  },
};

const kindModel = <C extends FilterColumn>(
  column: C
): FilterKindModel<FilterValue<C>> => FILTER_KINDS[FILTER_COLUMNS[column].kind];

// The custom matchers below read nothing but their arguments, so they are registered once, when
// this module loads, for every table instance. Their names are FILTER_COLUMNS' custom match modes.

const matchesEcolabel = (value: string[], filters: string[]): boolean => {
  if (!filters || !filters.length) return true;
  if (filters.includes(NONE_FILTER_VALUE) && (!value || value.length === 0))
    return true;
  return filters.some((f) => f !== NONE_FILTER_VALUE && value?.includes(f));
};

const matchesPrice = (value: string | null, filters: string[]): boolean => {
  if (!filters || !filters.length) return true;
  if (value == null) return filters.includes(NONE_FILTER_VALUE);
  return filters.includes(value);
};

const matchesAccountEmails = (
  value: string[] | null | undefined,
  filters: string[]
): boolean => {
  if (!filters || !filters.length) return true;
  if (!value || !value.length) return false;
  return value.some((email) => filters.includes(email));
};

FilterService.register("accountEmails", matchesAccountEmails);
FilterService.register("ecolabel", matchesEcolabel);
FilterService.register("codAge", matchesCodAge);
FilterService.register("limitPrice", matchesPrice);
FilterService.register("salePrice", matchesPrice);
